import { supabase } from './supabase';

// ==================== AAL / Status Checks ====================

/**
 * Check the current MFA assurance level for the user.
 * Returns currentLevel (aal1/aal2) and nextLevel (what's possible).
 */
export async function checkMfaStatus() {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw error;
  return {
    currentLevel: data.currentLevel,
    nextLevel: data.nextLevel,
    needsChallenge: data.nextLevel === 'aal2' && data.currentLevel === 'aal1',
    isVerified: data.currentLevel === 'aal2',
  };
}

/**
 * List all MFA factors for the current user.
 */
export async function getMfaFactors() {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  return {
    totp: data.totp,
    phone: data.phone,
    all: [...data.totp, ...data.phone],
    hasVerifiedFactor: [...data.totp, ...data.phone].some(f => f.status === 'verified'),
  };
}

/**
 * Check if the user has 2FA enabled in any panel (cross-panel check).
 */
export async function checkCrossPanelMfa(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('multibot_2fa_settings')
    .select('is_enabled')
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error checking cross-panel 2FA:', error);
    return false;
  }

  return data?.is_enabled ?? false;
}

// ==================== Enrollment ====================

/**
 * Start TOTP enrollment. Returns QR code and factor details.
 */
export async function enrollTotp(friendlyName?: string) {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: friendlyName || `SMS-Panel-${Date.now()}`,
  });
  if (error) throw error;
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
    uri: data.totp.uri,
  };
}

/**
 * Unenroll a factor by ID.
 */
export async function unenrollFactor(factorId: string) {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw error;
}

// ==================== Challenge & Verify ====================

/**
 * Create a challenge and verify the TOTP code.
 */
export async function challengeAndVerify(factorId: string, code: string) {
  const challenge = await supabase.auth.mfa.challenge({ factorId });
  if (challenge.error) throw challenge.error;

  const verify = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.data.id,
    code,
  });
  if (verify.error) throw verify.error;

  return verify.data;
}

// ==================== Trusted Devices ====================

/**
 * Generate a simple device fingerprint from browser info.
 */
export function getDeviceFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth.toString(),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.hardwareConcurrency?.toString() || '',
    navigator.platform || '',
  ];
  // Simple hash
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Get a human-readable device name.
 */
export function getDeviceName(): string {
  const ua = navigator.userAgent;
  let browser = 'Unknown Browser';
  let os = 'Unknown OS';

  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';

  if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return `${browser} on ${os}`;
}

/**
 * Check if the current device is trusted for the user.
 * Returns true if a valid (non-expired) trusted device record exists.
 */
export async function checkTrustedDevice(userId: string): Promise<boolean> {
  const fingerprint = getDeviceFingerprint();

  const { data, error } = await supabase
    .from('trusted_devices')
    .select('id, expires_at')
    .eq('user_id', userId)
    .eq('device_fingerprint', fingerprint)
    .gte('expires_at', new Date().toISOString())
    .limit(1);

  if (error) {
    console.error('Error checking trusted device:', error);
    return false;
  }

  if (data && data.length > 0) {
    // Update last_used_at
    await supabase
      .from('trusted_devices')
      .update({ last_used_at: new Date().toISOString() })
      .eq('id', data[0].id);
    return true;
  }

  return false;
}

/**
 * Add the current device as trusted for 30 days.
 */
export async function addTrustedDevice(userId: string): Promise<void> {
  const fingerprint = getDeviceFingerprint();
  const deviceName = getDeviceName();
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 30);

  // Upsert to handle duplicate fingerprints
  const { error } = await supabase
    .from('trusted_devices')
    .upsert(
      {
        user_id: userId,
        device_fingerprint: fingerprint,
        device_name: deviceName,
        browser_info: navigator.userAgent.substring(0, 200),
        expires_at: expiresAt.toISOString(),
        last_used_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,device_fingerprint' }
    );

  if (error) {
    console.error('Error adding trusted device:', error);
  }
}

/**
 * Get all trusted devices for the user.
 */
export async function getTrustedDevices(userId: string) {
  const { data, error } = await supabase
    .from('trusted_devices')
    .select('*')
    .eq('user_id', userId)
    .order('last_used_at', { ascending: false });

  if (error) {
    console.error('Error fetching trusted devices:', error);
    return [];
  }

  return data || [];
}

/**
 * Remove a trusted device.
 */
export async function removeTrustedDevice(deviceId: string) {
  const { error } = await supabase
    .from('trusted_devices')
    .delete()
    .eq('id', deviceId);

  if (error) throw error;
}

// ==================== RPC Helpers ====================

/**
 * Enable MFA for the current user (updates user_mfa_settings).
 */
export async function enableMfaForUser(userId: string) {
  const { error } = await supabase.rpc('enable_mfa_for_user', { p_user_id: userId });
  if (error) throw error;
}

/**
 * Disable MFA for the current user (updates user_mfa_settings + removes trusted devices).
 */
export async function disableMfaForUser(userId: string) {
  const { error } = await supabase.rpc('disable_mfa_for_user', { p_user_id: userId });
  if (error) throw error;
}

/**
 * Update multibot_2fa_settings to reflect cross-panel status.
 */
export async function updateCrossPanelMfa(userId: string, isEnabled: boolean) {
  const { error } = await supabase
    .from('multibot_2fa_settings')
    .upsert(
      {
        user_id: userId,
        is_enabled: isEnabled,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );

  if (error) {
    console.error('Error updating cross-panel 2FA:', error);
  }
}
